import { useEffect, useReducer, useRef, useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import * as customersApi from '@/api/customers'
import { ImageToolbarButton } from '@/components/ImageToolbarButton'
import { RichTextListButtons } from '@/components/RichTextListButtons'
import { buildRichTextExtensions } from '@/lib/richTextExtensions'
import {
  handleRichTextImageDrop,
  handleRichTextImagePaste,
} from '@/lib/richTextImages'
import {
  RICH_TEXT_COLOURS,
  RICH_TEXT_FONT_SIZES,
  applyColour,
  applyFontSize,
  captureEditorSelection,
} from '@/lib/richTextStyles'

const COLOURS = RICH_TEXT_COLOURS
const FONT_SIZES = RICH_TEXT_FONT_SIZES

function ToolbarButton({ active, disabled, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={[
        'rounded border px-2 py-1 text-xs font-semibold',
        active ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white text-brand hover:bg-slate-50',
        disabled ? 'opacity-40' : '',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

/**
 * Rich text editor for a layout block.
 * - mode "page": load/save via category page API (main notes)
 * - mode "inline": content lives on the block (content_html); pass onSaveInline to persist
 */
export function RichTextBlock({
  customerId,
  category,
  canEdit,
  mode = 'page',
  contentHtml = '',
  onInlineChange,
  onSaveInline,
}) {
  const [loading, setLoading] = useState(mode === 'page')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [savedAt, setSavedAt] = useState(null)
  const [initialHtml, setInitialHtml] = useState(mode === 'inline' ? contentHtml || '' : '')
  const [, bump] = useReducer((n) => n + 1, 0)
  const savedSelection = useRef(null)
  const editorRef = useRef(null)

  const editor = useEditor({
    extensions: buildRichTextExtensions(),
    content: mode === 'inline' ? contentHtml || '<p></p>' : '',
    editable: canEdit,
    editorProps: {
      attributes: {
        class: 'prose-kb min-h-[10rem] max-w-none px-3 py-2 focus:outline-none',
      },
      handlePaste: (_view, event) =>
        handleRichTextImagePaste(editorRef.current, _view, event, setError),
      handleDrop: (_view, event) =>
        handleRichTextImageDrop(editorRef.current, _view, event, setError),
    },
    onSelectionUpdate: () => bump(),
    onTransaction: () => bump(),
    onUpdate: ({ editor: ed }) => {
      if (mode === 'inline' && onInlineChange) {
        onInlineChange(ed.getHTML())
      }
    },
  })

  useEffect(() => {
    editorRef.current = editor
  }, [editor])

  useEffect(() => {
    if (editor) editor.setEditable(canEdit)
  }, [editor, canEdit])

  useEffect(() => {
    if (mode !== 'page') {
      setLoading(false)
      return undefined
    }
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const page = await customersApi.getPage(customerId, category.slug)
        if (cancelled) return
        const html = page.content_html || ''
        setInitialHtml(html)
        if (editor) editor.commands.setContent(html || '<p></p>', false)
        setSavedAt(page.updated_at)
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load page.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [customerId, category.slug, editor, mode])

  async function handleSave() {
    if (!editor) return
    setSaving(true)
    setError('')
    try {
      const html = editor.getHTML()
      if (mode === 'page') {
        const page = await customersApi.savePage(customerId, category.slug, html)
        setInitialHtml(html)
        setSavedAt(page.updated_at)
      } else if (onSaveInline) {
        await onSaveInline(html)
        setInitialHtml(html)
        setSavedAt(new Date().toISOString())
      }
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save page.',
      )
    } finally {
      setSaving(false)
    }
  }

  const dirty =
    canEdit &&
    editor &&
    editor.getHTML() !== (initialHtml || '<p></p>') &&
    editor.getHTML() !== initialHtml

  const canSaveNotes = mode === 'page' || Boolean(onSaveInline)

  const inTable = Boolean(editor?.isActive('table'))
  const currentColour = editor?.getAttributes('textStyle')?.color || ''
  const currentSize = editor?.getAttributes('textStyle')?.fontSize || ''

  if (loading) {
    return <p className="text-sm text-brand-muted">Loading…</p>
  }

  return (
    <div className="space-y-2">
      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      {canEdit && editor && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5">
          <ToolbarButton
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            Bold
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            Underline
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            Italic
          </ToolbarButton>
          <RichTextListButtons editor={editor} ToolbarButton={ToolbarButton} />
          <label className="flex items-center gap-1 text-xs text-brand-muted">
            Size
            <select
              className="rounded border border-slate-300 bg-white px-1 py-1 text-xs text-brand"
              value={currentSize}
              onMouseDown={() => {
                savedSelection.current = captureEditorSelection(editor)
              }}
              onChange={(e) => {
                applyFontSize(editor, e.target.value, savedSelection.current)
              }}
            >
              {FONT_SIZES.map((s) => (
                <option key={s.label} value={s.value}>
                  {s.label}
                </option>
              ))}
              {currentSize && !FONT_SIZES.some((s) => s.value === currentSize) && (
                <option value={currentSize}>Custom ({currentSize})</option>
              )}
            </select>
          </label>
          <label className="flex items-center gap-1 text-xs text-brand-muted">
            Colour
            <select
              className="rounded border border-slate-300 bg-white px-1 py-1 text-xs text-brand"
              value={currentColour}
              onMouseDown={() => {
                savedSelection.current = captureEditorSelection(editor)
              }}
              onChange={(e) => {
                applyColour(editor, e.target.value, savedSelection.current)
              }}
            >
              {COLOURS.map((c) => (
                <option key={c.label} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <ToolbarButton
            active={inTable}
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
          >
            Table
          </ToolbarButton>
          <ToolbarButton
            disabled={!inTable}
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          >
            + Col
          </ToolbarButton>
          <ToolbarButton
            disabled={!inTable}
            onClick={() => editor.chain().focus().deleteColumn().run()}
          >
            − Col
          </ToolbarButton>
          <ToolbarButton
            disabled={!inTable}
            onClick={() => editor.chain().focus().addRowAfter().run()}
          >
            + Row
          </ToolbarButton>
          <ToolbarButton
            disabled={!inTable}
            onClick={() => editor.chain().focus().deleteRow().run()}
          >
            − Row
          </ToolbarButton>
          <ToolbarButton
            disabled={!inTable}
            onClick={() => editor.chain().focus().deleteTable().run()}
          >
            Delete table
          </ToolbarButton>
          <ImageToolbarButton
            editor={editor}
            ToolbarButton={ToolbarButton}
            onError={setError}
          />
          {canSaveNotes && (
            <button
              type="button"
              disabled={saving || !dirty}
              onClick={handleSave}
              className="ml-auto rounded-md bg-brand px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {saving ? 'Saving…' : dirty ? 'Save notes' : 'Saved'}
            </button>
          )}
          {canSaveNotes && savedAt && !dirty && (
            <span className="text-xs text-brand-muted">
              {new Date(savedAt).toLocaleString('en-GB')}
            </span>
          )}
        </div>
      )}

      <div className="rounded-lg border border-slate-300 bg-white shadow-sm">
        {editor ? (
          <EditorContent editor={editor} />
        ) : (
          <p className="px-3 py-2 text-sm text-brand-muted">No content yet.</p>
        )}
      </div>
    </div>
  )
}
